import { CALLER_DEFAULT_UPLOAD } from '@/lib/files/paths';
import type { SharedItem, ToolSharingState } from '@/store/slices/toolSharingSlice';

export const FILE_GRANT_TOPIC = 'file-grant.v2';
export type FileGrantContext = { version: 2; callRowId: string | null; roomName: string };
export type FileGrantScope = { epoch: string; revision: string; operationId: string };
export type FileGrantTarget = { callRowId: string; roomName: string; participantIdentity: string; participantSid: string; requestNonce: string };
export type FileGrantRequest = FileGrantTarget & { type: 'request' };
export type FileGrantReceipt = { type: 'receipt'; target: FileGrantTarget; scope: FileGrantScope;
  generation: string; expiresAtMs: string; accessToken: string };
export const isGrantUuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(v);
const nonempty = (v: unknown): v is string => typeof v === 'string' && v !== '';
const positive = (v: unknown): v is string => typeof v === 'string' && /^[1-9][0-9]*$/.test(v);
const fields = (v: unknown, names: string[]): v is Record<string, unknown> => !!v && typeof v === 'object' &&
  !Array.isArray(v) && Object.keys(v).length === names.length && names.every(k => Object.prototype.hasOwnProperty.call(v, k));
export function isFileGrantScope(v: unknown): v is FileGrantScope {
  return fields(v, ['epoch', 'revision', 'operationId']) && isGrantUuid(v.epoch) && isGrantUuid(v.operationId) &&
    positive(v.revision) && BigInt(v.revision) <= 9223372036854775807n;
}
function target(v: unknown): v is FileGrantTarget {
  return fields(v, ['callRowId', 'roomName', 'participantIdentity', 'participantSid', 'requestNonce']) &&
    isGrantUuid(v.callRowId) && isGrantUuid(v.requestNonce) && nonempty(v.roomName) && nonempty(v.participantIdentity) &&
    typeof v.participantSid === 'string' && /^PA_[A-Za-z0-9]+$/.test(v.participantSid);
}
export function parseFileGrantPacket(v: unknown): FileGrantRequest | FileGrantReceipt | null {
  if (fields(v, ['type', 'callRowId', 'roomName', 'participantIdentity', 'participantSid', 'requestNonce']) && v.type === 'request') {
    const { type: _, ...t } = v;
    return target(t) ? v as FileGrantRequest : null;
  }
  if (fields(v, ['type', 'target', 'scope', 'generation', 'expiresAtMs', 'accessToken']) && v.type === 'receipt' &&
      target(v.target) && isFileGrantScope(v.scope) && positive(v.generation) && positive(v.expiresAtMs) &&
      BigInt(v.expiresAtMs) <= 8640000000000000n && typeof v.accessToken === 'string' &&
      /^caller-v2\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(v.accessToken)) return v as FileGrantReceipt;
  return null;
}
export function sameGrantScope(a: FileGrantScope | null | undefined, b: FileGrantScope | null | undefined): boolean {
  return !!a && !!b && a.epoch === b.epoch && a.revision === b.revision && a.operationId === b.operationId;
}
export function matchesGrantTarget(a: FileGrantTarget, b: FileGrantTarget): boolean {
  return Object.keys(a).every(k => a[k as keyof FileGrantTarget] === b[k as keyof FileGrantTarget]);
}
/** Keep the complete ordered ACL, including explicit empty recording track. */
export function fileGrantPermissions(state: Pick<ToolSharingState, 'sharedItems' | 'callerUploadFolder'>) {
  const sharedItems = state.sharedItems.map((item: SharedItem) => {
    const value: Record<string, string> = { path: item.path, type: item.type };
    for (const field of ['recordingAttemptId', 'recordingViewType', 'recordingAudioVariant', 'recordingTrackSid'] as const) {
      if (Object.prototype.hasOwnProperty.call(item, field)) value[field] = item[field]!;
    }
    return value;
  });
  return { allowedPaths: [...new Set([...state.sharedItems.map(i => i.path), state.callerUploadFolder])],
    uploadFolder: state.callerUploadFolder, sharedItems };
}
export function fileGrantPermissionsKey(value: ReturnType<typeof fileGrantPermissions>): string {
  return JSON.stringify([value.allowedPaths, value.uploadFolder, value.sharedItems.map(item =>
    ['path', 'type', 'recordingAttemptId', 'recordingViewType', 'recordingAudioVariant', 'recordingTrackSid']
      .map(key => Object.prototype.hasOwnProperty.call(item, key) ? [key, item[key]] : [key]))]);
}
/** Complete durable selection shared by checkpoint, DB, full-state and archive.
 * Runtime intent/call bindings, availability, UI state and grant-mode authority
 * never enter this projection. Legacy partials inherit only omitted fields.
 */
export type DurableToolSharing = Pick<ToolSharingState, 'sharedTools' | 'activeTool' | 'isSharing' | 'callerReadOnly' | 'sharedFiles' | 'sharedItems' | 'callerUploadFolder' | 'fileBrowserAccessToken' | 'lastUpdated' | 'backgroundRevokedAt'>;
const SHARING_TOOLS = ['map', 'canvas', 'files', 'background'] as const;
const sharingRecord = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object' && !Array.isArray(value);
const finiteSharingClock = (value: unknown, fallback = 0): number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : fallback;
export function projectDurableToolSharing(value: unknown, options: { previous?: unknown; selectedV2?: boolean } = {}): DurableToolSharing | null {
  if (!sharingRecord(value)) return null;
  const prior = sharingRecord(options.previous) && options.previous.selectionIntent !== 'unresolved' && options.previous.selectionIntent !== 'implicit-background' ? options.previous : {};
  const merged = { ...prior, ...value };
  const tools = Array.isArray(merged.sharedTools) ? merged.sharedTools : merged.activeTool ? [merged.activeTool] : [];
  const sharedTools = tools.filter((tool: unknown) => SHARING_TOOLS.includes(tool as any));
  const token = merged.fileBrowserAccessToken;
  const privateMode = options.selectedV2 || value.fileGrantMode?.version === 2 || prior.fileGrantMode?.version === 2;
  return {
    sharedTools, activeTool: sharedTools[0] ?? null, isSharing: sharedTools.length > 0,
    callerReadOnly: typeof merged.callerReadOnly === 'boolean' ? merged.callerReadOnly : false,
    sharedFiles: Array.isArray(merged.sharedFiles) ? merged.sharedFiles : [],
    sharedItems: Array.isArray(merged.sharedItems) ? merged.sharedItems : [],
    callerUploadFolder: typeof merged.callerUploadFolder === 'string' ? merged.callerUploadFolder : CALLER_DEFAULT_UPLOAD,
    fileBrowserAccessToken: !privateMode && typeof token === 'string' && !token.startsWith('caller-v2.') ? token : null,
    lastUpdated: finiteSharingClock(merged.lastUpdated, finiteSharingClock(prior.lastUpdated)),
    backgroundRevokedAt: finiteSharingClock(merged.backgroundRevokedAt, finiteSharingClock(prior.backgroundRevokedAt)),
  };
}
/** null/absence/transient input is non-authorship, not an empty selection. */
export function mergeDurableToolSharing(previous: unknown, incoming: unknown, selectedV2 = false): DurableToolSharing | null {
  if (!sharingRecord(incoming) || incoming.selectionIntent === 'unresolved' || incoming.selectionIntent === 'implicit-background') {
    return sharingRecord(previous) && previous.selectionIntent !== 'unresolved' && previous.selectionIntent !== 'implicit-background' ? projectDurableToolSharing(previous, { selectedV2 }) : null;
  }
  return projectDurableToolSharing(incoming, { previous, selectedV2 });
}

/** Runtime mode is never hydrated; v2 credentials never enter durable/public state. */
export function projectToolSharing<T extends object>(value: T, selectedV2: boolean): T {
  return Object.fromEntries(Object.entries(value).filter(([key]) => key !== 'fileGrantMode' && key !== 'selectionIntent' && key !== 'selectionCallId' &&
    (!selectedV2 || (key !== 'fileBrowserAccessToken' && key !== 'file_browser_access_token')))) as T;
}
/** In v2, ordinary peer packets cannot become the lead's file ACL intent.
 * Tokenless refreshes without a new scope preserve the caller's file display. */
export function preservePrivateFileSelection<T extends Record<string, any>>(message: T, state: ToolSharingState,
  selectedV2: boolean, isOfficer: boolean): T {
  if (!selectedV2 || (!isOfficer && Object.prototype.hasOwnProperty.call(message, 'fileGrantScope'))) return message;
  const tools = Array.isArray(message.sharedTools) ? message.sharedTools.filter((tool: string) => tool !== 'files') :
    state.sharedTools.filter(tool => tool !== 'files');
  return { ...message, sharedTools: [...tools, ...(state.sharedTools.includes('files') ? ['files'] : [])],
    sharedFiles: state.sharedFiles, sharedItems: state.sharedItems, callerUploadFolder: state.callerUploadFolder };
}
